import React, { FC, useState } from 'react';
import classnames from 'classnames';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useParams, NavLink } from 'react-router-dom';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from '../../Button/button';
import Card from 'buyside/components/presentation/Card/card';
import JobApplicationIndexPage from 'buyside/components/presentation/JobApplicationIndexPage/JobApplicationIndexPage';

import { formatDate } from 'buyside/util/dateHelper';
const { jobPostingStatuses } = require('buyside/util/constantsFromRails.json');
import { jobVaultNewUrl, jobVaultPostingUrl } from 'buyside/util/urlHelper';

import styles from 'buyside/components/presentation/UserAccount/JobsPosted/userJobPostingsPageStyles.scss';

import { useCurrentUserAccountJobPostingsQuery } from 'interfaces/graphql-typings';

import LoadingSpinner, {
  SIZES
} from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';

const UserJobPostingsPage: FC<{}> = () => {
  const { loading, data } = useCurrentUserAccountJobPostingsQuery();

  const { jobPostingId } = useParams();

  const jobPostings = data?.currentUser?.jobPostings;

  const currentPosting = jobPostings?.find(
    jobPosting => jobPosting?.id && jobPosting?.id === jobPostingId
  );

  const [hideApplications, toggleHideApplications] = useState(false);
  if (loading || !data?.currentUser) return <LoadingSpinner size={SIZES.LARGE} />;

  return (
    <>
      <div className={styles.pageTitle}>Jobs Posted</div>
      <Button
        type={BUTTON_TYPES.PRIMARY}
        size={BUTTON_SIZES.MEDIUM}
        className={styles.newJobPostingLink}
        url={jobVaultNewUrl()}
        text="New Job Posting"
      />
      <div className={styles.pageContent}>
        {!jobPostings?.length ? (
          <p className={styles.noJobPostings}>
            You have no job postings at the moment. If you have an opportunity coming up that you
            would like to share with the SumZero Community, please visit the{' '}
            <a href={jobVaultNewUrl()}>Job Vault</a> to create a new posting.
          </p>
        ) : (
          <>
            {!currentPosting && (
              <p className={styles.noPostingSelected}>
                Select one of your job postings to manage current applicants.
              </p>
            )}

            <section className={styles.navContainer}>
              {currentPosting && (
                <Link className={styles.expandIcon} to="/job_postings">
                  <FontAwesomeIcon icon={faArrowLeft} />
                </Link>
              )}

              <nav
                className={classnames(styles.jobPostingNav, {
                  [styles.selected]: Boolean(currentPosting)
                })}
              >
                {jobPostings?.map(jobPosting => {
                  if (!jobPosting) return null;
                  const { id, title, postedAt, applicationCount, status, updatedAt } = jobPosting;

                  return (
                    <NavLink
                      data-testid="posting-nav-link"
                      key={id}
                      to={`/job_postings/${id}`}
                      className={({ isActive }) =>
                        classnames(styles.navLink, {
                          [styles.navLinkUnselected]: !isActive,
                          [styles.navLinkSelected]: isActive
                        })
                      }
                      onClick={() => toggleHideApplications(status === jobPostingStatuses.filled)}
                    >
                      <div className={styles.titlePosted}>
                        <h5>{title}</h5>
                        <h6>
                          Posted: <span>{formatDate(postedAt)}</span>
                        </h6>
                      </div>
                      <div className={styles.navItemInfo}>
                        {Boolean(applicationCount) && status === jobPostingStatuses.active && (
                          <span className={styles.applicationCount}>{applicationCount} Apps</span>
                        )}
                        {status === jobPostingStatuses.filled && (
                          <span className={styles.statusFilled}>
                            Filled: {formatDate(updatedAt)}
                          </span>
                        )}
                      </div>
                    </NavLink>
                  );
                })}
              </nav>
            </section>
            <div className={styles.jobPostingContainer}>
              {currentPosting && currentPosting.status === jobPostingStatuses.filled && (
                <Card customStyles={styles.postingFilled}>
                  <div>
                    <header>
                      <h3>
                        <a href={jobVaultPostingUrl(currentPosting.id)}>{currentPosting.title}</a>
                      </h3>
                      <div className={styles.timeStamps}>
                        <div>
                          <h5>Posted:</h5>
                          <span>{formatDate(currentPosting.postedAt)}</span>
                        </div>
                        <div>
                          <h5>Filled:</h5>
                          <span>{formatDate(currentPosting.updatedAt)}</span>
                        </div>
                      </div>
                    </header>
                    <section>
                      <h5>POSITION HAS BEEN FILLED</h5>
                      <div className={styles.jobPostingFilledOptions}>
                        <Button
                          type={BUTTON_TYPES.SECONDARY}
                          size={BUTTON_SIZES.MEDIUM}
                          className={styles.viewPostingLink}
                          url={jobVaultPostingUrl(currentPosting.id)}
                          text="View Posting"
                        />
                        <Button
                          type={BUTTON_TYPES.SECONDARY}
                          size={BUTTON_SIZES.MEDIUM}
                          className={styles.toggleApplicationsButton}
                          onClick={(): void => toggleHideApplications(!hideApplications)}
                          text={`${hideApplications ? 'View' : 'Hide'} Applications`}
                        />
                      </div>
                    </section>
                  </div>
                </Card>
              )}
              {currentPosting && !hideApplications && (
                <div
                  className={classnames(styles.jobPosting, {
                    [styles.hideApplications]:
                      currentPosting.status === jobPostingStatuses.filled && hideApplications
                  })}
                >
                  <JobApplicationIndexPage
                    jobPostingId={currentPosting.id}
                    jobPostingTitle={currentPosting.title}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default UserJobPostingsPage;
